import { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { Redemption, StrategyKey, Valuation } from '../../api/types'
import { useOpsApi } from '../../api/context'
import { apiError } from '../../api/errors'
import { SectionCard } from '../../components/SectionCard'
import { formatDate, formatUnits } from './format'
import { formatCzk } from '../platby/format'

interface RedemptionQueueCardProps {
  fundKey: StrategyKey
  /** Poslední ocenění fondu — jeho cenou se fronta ocení. */
  latestValuation: Valuation | null
}

/**
 * Odkupy ve frontě fondu (Michal 14. 10.): odkup se vyřizuje při ocenění jednotek, stejně
 * jako vklady. Po zadání ocenění se tlačítkem ocení všechny žádosti, jejichž Den ocenění
 * nastal; potvrzuje je pak CEO v Kontrole přidělení.
 */
export function RedemptionQueueCard({ fundKey, latestValuation }: RedemptionQueueCardProps) {
  const api = useOpsApi()
  const [queue, setQueue] = useState<Redemption[]>([])
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(() => {
    api
      .listRedemptions({ fundKey, status: 'QUEUED' })
      .then(setQueue)
      .catch((err) => setError(apiError(err, 'Frontu odkupů se nepodařilo načíst')))
  }, [api, fundKey])

  useEffect(() => {
    setInfo(null)
    setError(null)
    load()
  }, [load, latestValuation])

  const ready = latestValuation
    ? queue.filter((r) => !r.valuationDay || r.valuationDay <= latestValuation.date)
    : []

  const handlePrice = async () => {
    if (!latestValuation) return
    setBusy(true)
    setError(null)
    try {
      const result = await api.priceRedemptionQueue(fundKey, latestValuation.id)
      const priced = result.filter((r) => r.status === 'REQUESTED').length
      const failed = result.filter((r) => r.status === 'QUEUED' && r.queueError).length
      setInfo(
        `Oceněno ${priced} odkupů cenou ${formatUnits(latestValuation.unitPrice)}. CEO je potvrdí v Kontrole přidělení.` +
          (failed ? ` ${failed} zůstalo ve frontě (důvod u žádosti).` : '')
      )
      load()
    } catch (err) {
      setError(apiError(err, 'Ocenění fronty odkupů selhalo'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <SectionCard title={`Odkupy ve frontě (${queue.length})`}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {error && <Alert severity="error">{error}</Alert>}
        {info && (
          <Alert severity="success" onClose={() => setInfo(null)}>
            {info}
          </Alert>
        )}
        {queue.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Žádné výběry ve frontě. Zadávají se ve Vklady / výběry. Do fronty jdou po potvrzení žádosti.
          </Typography>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Klient</TableCell>
                  <TableCell align="right">Odkup</TableCell>
                  <TableCell>Doručeno</TableCell>
                  <TableCell>Den ocenění</TableCell>
                  <TableCell>Poznámka</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {queue.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>{r.clientName}</TableCell>
                    <TableCell align="right">
                      {r.redeemAll
                        ? 'celá držba'
                        : r.requestedUnits
                        ? `${formatUnits(r.requestedUnits)} j.`
                        : r.requestedAmount
                        ? formatCzk(r.requestedAmount)
                        : ''}
                    </TableCell>
                    <TableCell>{r.receivedDate ? formatDate(r.receivedDate) : ''}</TableCell>
                    <TableCell>{r.valuationDay ? formatDate(r.valuationDay) : ''}</TableCell>
                    <TableCell sx={{ color: r.queueError ? 'error.main' : undefined }}>
                      {r.queueError || ''}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
        {queue.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Button variant="contained" onClick={handlePrice} disabled={busy || !latestValuation || ready.length === 0}>
              Ocenit odkupy ({ready.length})
            </Button>
            <Typography variant="caption" color="text.secondary">
              {latestValuation
                ? `Cenou ocenění z ${formatDate(latestValuation.date)}. Ocení se žádosti, jejichž Den ocenění už nastal.`
                : 'Nejdřív zadejte ocenění fondu.'}
            </Typography>
          </Box>
        )}
      </Box>
    </SectionCard>
  )
}
